// ─────────────────────────────────────────────────────────────────────────────
// Product Lines & Vendors (pantallas /master-data/product-lines y /master-data/vendors)
// — capa de datos (2026-10-19, conectada a endpoints reales 2026-10-20)
//
// Empresas: 3 = Mayaland (madre), 6 = Altitude (hija). Cada empresa tiene sus
// propios números; un cambio "en ambas" se manda una vez por empresa, con el
// IdCompany y el número de ESA empresa.
//
//   ESCRITURA → POST /Test/EnvioOrden (ruta base, sin prefijo). Crear y editar
//     usan el mismo OrderType (ImportProductLine / ImportVendor, upsert por
//     número en Sage). Responde cuando Sage y el sync del mirror terminaron,
//     por eso lleva un timeout largo.
//   LECTURA / VERIFICACIÓN → backv3 (api-portal-manager/src/v3_controllers/masterData.ts),
//     siempre contra la tabla mirror de la empresa:
//       /v3/master-data/list, /v3/master-data/exists (LIMIT 1), /v3/master-data/next-number
// ─────────────────────────────────────────────────────────────────────────────
import { getInformationWithDataPrefix, getInformationWithDataPrefixV3, getInformationWithDataV3 } from './CABE';
import { getValueCookie } from './cookieService';

export const COMPANIES = {
    3: { id: 3, name: 'Mayaland', role: 'Parent' },
    6: { id: 6, name: 'Altitude', role: 'Child' },
};
// Orden de envío: siempre primero la madre.
export const COMPANY_ORDER = [3, 6];
export const companyName = (id) => (COMPANIES[id] ? COMPANIES[id].name : `Company ${id}`);
export const otherCompany = (id) => (Number(id) === 3 ? 6 : 3);

export const MASTER_DATA_ENDPOINTS = {
    list: '/v3/master-data/list',               // backv3 · mirror por empresa
    exists: '/v3/master-data/exists',           // backv3 · mirror, LIMIT 1
    nextNumber: '/v3/master-data/next-number',  // backv3 · máximo numérico + 1
    send: '/Test/EnvioOrden',               // backv3 (prueba 2026-10-23; antes backv2) · worker MAS90 (+ sync del mirror)
};
export const ORDER_TYPES = { productLine: 'ImportProductLine', vendor: 'ImportVendor' };

// EnvioOrden espera a Sage + sync del mirror: 3 minutos antes de darlo por perdido.
export const SEND_TIMEOUT_MS = 180000;

const ok = (res) => !!(res && ((res.status && (res.status.code === 1 || res.status === 'success')) || res.code === 1));
const msgOf = (res) => (res && (res.message || res.error || (res.status && res.status.Description) || res.Description)) || '';

const str = (v) => String(v === undefined || v === null ? '' : v).trim();
const pick = (obj, keys) => {
    for (let i = 0; i < keys.length; i++) {
        const v = obj[keys[i]];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
};
const rowsOf = (res) => {
    if (!res) return [];
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.data)) return res.data.data;
    return [];
};

// ─── Normalizadores (fila del mirror → { code, name, ... }) ──────────────────
// Varios nombres posibles por columna para no depender del esquema exacto.
const normalize = (kind, r) => (kind === 'vendor'
    ? {
        code: str(pick(r, ['VendorNo', 'vendorNo'])),
        name: str(pick(r, ['VendorName', 'vendorName'])),
        address: str(pick(r, ['AddressLine1', 'addressLine1'])),
        city: str(pick(r, ['City', 'city'])),
        state: str(pick(r, ['State', 'state'])),
        zip: str(pick(r, ['ZipCode', 'zipCode', 'Zip'])),
        phone: str(pick(r, ['TelephoneNo', 'telephoneNo', 'Phone'])),
        email: str(pick(r, ['EmailAddress', 'emailAddress', 'Email'])),
    }
    : {
        code: str(pick(r, ['ProductLine', 'productLine'])),
        name: str(pick(r, ['ProductLineDesc', 'productLineDesc'])),
    });

// ─── Lecturas (mirror) ───────────────────────────────────────────────────────
export const listEntities = async (kind, idCompany) => {
    const res = await getInformationWithDataV3(MASTER_DATA_ENDPOINTS.list, { kind, idCompany });
    if (!ok(res)) return { ok: false, rows: [], message: msgOf(res) || `Could not load the list from ${companyName(idCompany)}.` };
    return { ok: true, rows: rowsOf(res).map((r) => normalize(kind, r)).filter((r) => r.code) };
};

// { ok, exists, record }. ok=false → no se pudo consultar; NO significa "no existe".
export const checkExists = async (kind, idCompany, code) => {
    const res = await getInformationWithDataV3(MASTER_DATA_ENDPOINTS.exists, { kind, idCompany, code });
    if (!ok(res)) return { ok: false, exists: false, record: null, message: msgOf(res) || 'No response from the server.' };
    const d = res.data || {};
    return { ok: true, exists: !!d.exists, record: d.record ? normalize(kind, d.record) : null };
};

export const getNextNumber = async (kind, idCompany) => {
    const res = await getInformationWithDataV3(MASTER_DATA_ENDPOINTS.nextNumber, { kind, idCompany });
    const value = ok(res) && res.data && res.data.nextNo ? String(res.data.nextNo) : '';
    return value ? { ok: true, value } : { ok: false, message: msgOf(res) || `Could not get the next number for ${companyName(idCompany)}.` };
};

// ─── Escritura (EnvioOrden) ──────────────────────────────────────────────────
// form: { code, name, ...vendor: address, city, state, zip, phoneDigits, email }
const buildPayload = (kind, idCompany, form) => {
    const base = { OrderType: ORDER_TYPES[kind], IdCompany: idCompany, updateBy: getValueCookie('userName') };
    if (kind === 'productLine') {
        // El worker elige el .bat con CompanyId === "6" (string).
        return { ...base, CompanyId: String(idCompany), ProductLine: form.code, ProductLineDesc: form.name };
    }
    // El worker elige el .bat con idcompany === "6" (minúscula y string).
    return {
        ...base,
        idcompany: String(idCompany),
        VendorNo: form.code,
        VendorName: form.name,
        VendorAddress: form.address,
        VendorCity: form.city,
        VendorState: form.state,
        VendorZip: form.zip,
        VendorCountry: 'USA',
        VendorPhone: form.phoneDigits,
        VendorEmail: form.email,
    };
};

// Devuelve { ok, timedOut, message }. Pase lo que pase, la pantalla confirma
// después contra el mirror: la respuesta sola no basta.
export const sendToMas90 = async (kind, idCompany, form) => {
    // TODO: Revisar backv3 cuando se terminen de comprobar los nuevos puertos
    // backv2 (regresar aquí si falla la prueba): 
    const res = await getInformationWithDataPrefix(MASTER_DATA_ENDPOINTS.send, buildPayload(kind, idCompany, form), '', SEND_TIMEOUT_MS);
    // const res = await getInformationWithDataPrefixV3(MASTER_DATA_ENDPOINTS.send, buildPayload(kind, idCompany, form), '', SEND_TIMEOUT_MS);
    return { ok: ok(res), timedOut: !!(res && res.code === 'TIMEOUT'), message: ok(res) ? '' : msgOf(res) };
};
